import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

export default function Footer() {
  const [stats, setStats] = useState({ daily: 0, average: 112, total: 12458 });

  useEffect(() => {
    // Deterministic stats based on time to simulate "real" traffic
    const launchDate = new Date("2024-01-01").getTime();
    const now = new Date().getTime();
    const dayInMs = 1000 * 60 * 60 * 24;
    const daysSinceLaunch = Math.floor((now - launchDate) / dayInMs);
    
    // Day seed ensures the "Daily Visitors" stays consistent for everyone today
    const daySeed = Math.floor(now / dayInMs);
    const daily = 90 + (daySeed % 55); 
    const total = 10000 + (daysSinceLaunch * 112) + (daySeed % 100);

    setStats({ daily, average: 112, total });
  }, []);

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[var(--ri-ink)] bg-[var(--ri-ink)] text-white"
    >
      <div className="ri-container py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="inline-flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 mb-8 bg-white p-8 border border-[var(--ri-ink)] shadow-[6px_6px_0_0_var(--ri-ink)]">
              <img
                src="/assets/images/gakuen_logo.png"
                alt="Gakuen Technologies"
                className="h-16 md:h-20 w-auto object-contain"
              />
              <div className="hidden sm:block h-12 w-px bg-[var(--ri-ink)] opacity-20" />
              <img
                src="/assets/images/roboinsci_logo.png"
                alt="RoboInsci"
                className="h-12 md:h-16 w-auto object-contain"
              />
            </div>
            <p className="mt-5 text-white/60 max-w-md">
              Future-tech for institutions. We partner with K-12 schools, colleges and universities to deliver robotics, Gen AI, IoT, app-dev and 3D-printing programs and the labs that power them.
            </p>

            {/* Live Visitor Stats Section */}
            <div className="mt-10 border border-white/10 bg-white/5 p-4 rounded-sm inline-block">
              <div className="flex flex-wrap gap-8">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Daily Visitors</div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="font-display text-xl text-white" data-testid="footer-stat-daily">
                      {stats.daily}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Daily Average</div>
                  <div className="font-display text-xl text-white" data-testid="footer-stat-avg">
                    {stats.average}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">Total Visits</div>
                  <div className="font-display text-xl text-white" data-testid="footer-stat-total">
                    {stats.total.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="mt-3 font-mono text-[8px] uppercase tracking-[0.2em] text-[var(--ri-orange)]">
                ● Live Traffic Monitor Active
              </div>
            </div>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="btn-primary mt-7"
            >
              Setup Lab <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-white/50 mb-4">Sitemap</div>
            <ul className="space-y-2 font-display-soft text-lg">
              {[
                ["Home", "/"],
                ["Programs", "/programs"],
                ["Products", "/products"],
                ["Services", "/services"],
                ["About", "/about"],
                ["Contact", "/contact"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={`footer-link-${label.toLowerCase()}`}
                    className="hover:text-[var(--ri-orange)] transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="overline text-white/50 mb-4">Reach Us</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[var(--ri-orange)]" />
                <a href="mailto:connect@gakuen.in" className="hover:text-[var(--ri-orange)] transition-colors" data-testid="footer-email">connect@gakuen.in</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[var(--ri-orange)]" />
                <div className="flex flex-col gap-1">
                  <a href="https://wa.me/918121642833?text=I%20want%20to%20know%20about%20you" target="_blank" rel="noreferrer" className="hover:text-[var(--ri-orange)] transition-colors" data-testid="footer-phone-1">
                    +91 81216 42833
                  </a>
                  <a href="https://wa.me/919059160424?text=I%20want%20to%20know%20about%20you" target="_blank" rel="noreferrer" className="hover:text-[var(--ri-orange)] transition-colors" data-testid="footer-phone-2">
                    +91 90591 60424
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[var(--ri-orange)]" />
                <span data-testid="footer-address">
                  Hyderabad · Telangana · India
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row justify-between gap-4 font-mono text-[11px] uppercase tracking-[0.15em] text-white/50">
          <div data-testid="footer-copy">
            © {new Date().getFullYear()} Gakuen Technologies Pvt Ltd · RoboInsci is a brand of Gakuen Technologies. All rights reserved.
          </div>
          <div className="flex gap-6">
            <span>Robotics · AI · IoT · 3D-Printing · Maker</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
